import { useContext, useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import { customDomainThemeModes } from 'app/frontend/types/custom/CustomDomainThemeMode'
import ColorField from 'app/frontend/base/components/form-fields/ColorField'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  themeDefaults,
  themeVariables,
} from 'app/frontend/stylesheets/theme-defaults'
import { FormEntityContext } from 'app/frontend/base/config/context/forms'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useTranslation } from 'react-i18next'
import Card from 'app/frontend/base/components/Card'
import { Label } from '@/components/ui/label'
import { Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTheme } from '@/components/theme-provider'
import { useCustomDomain } from 'app/frontend/base/hooks/useCustomDomain'
import FileField from 'app/frontend/base/components/form-fields/FileField'
import { useTeam } from 'app/frontend/base/config/context/team'

type ThemeColorFieldsProps = {
  previewing: boolean
  selectedTab: string
  setSelectedTab: (tab: string) => void
}

const applyThemeVars = (
  attrs: Record<string, Record<string, string>>,
  scheme: 'light' | 'dark',
) => {
  removeThemeVars()
  const modeTheme =
    scheme === 'dark' ? attrs.darkModeTheme : attrs.lightModeTheme
  ;[attrs.generalTheme, modeTheme].forEach((theme) => {
    Object.entries(theme ?? {}).forEach(([key, value]) => {
      if (value) document.documentElement.style.setProperty(key, value)
    })
  })
}

const removeThemeVars = () => {
  themeVariables.forEach((key) => {
    document.documentElement.style.removeProperty(key)
  })
  document.documentElement.style.removeProperty('--radius-sm-custom')
  document.documentElement.style.removeProperty('--font-family-custom')
}

export const ThemeModeField = ({ previewing }: { previewing: boolean }) => {
  const form = useFormContext()
  const { customDomain, setCustomDomainPreview } = useCustomDomain()
  const { setTheme, currentScheme } = useTheme()

  return (
    <SelectField
      layout='box'
      customPlaceholder='Dark'
      name='customDomainAttributes.themeMode'
      options={customDomainThemeModes}
      onChange={(value) => {
        if (!previewing) return

        if (customDomain) {
          setCustomDomainPreview({
            ...customDomain,
            themeMode: value as 'light' | 'dark',
          })
        }

        const scheme =
          value !== 'both' ? (value as 'light' | 'dark') : currentScheme
        if (value !== 'both') {
          setTheme(scheme)
        }

        applyThemeVars(form.getValues('customDomainAttributes'), scheme)
      }}
    />
  )
}

export const PreviewToggle = ({
  previewing,
  onToggle,
  selectedTab,
}: {
  previewing: boolean
  onToggle: (value: boolean) => void
  selectedTab: string
}) => {
  const { guild, team } = useTeam()
  const { setCustomDomainPreview } = useCustomDomain()
  const form = useFormContext()
  const { setTheme, currentScheme } = useTheme()
  const themeBeforePreview = useRef<'light' | 'dark'>(currentScheme)
  const previewingRef = useRef(previewing)
  previewingRef.current = previewing

  useEffect(() => {
    return () => {
      if (previewingRef.current) {
        removeThemeVars()
        setTheme(themeBeforePreview.current)
        setCustomDomainPreview(undefined)
      }
    }
  }, [])

  return (
    <Button
      variant='outline'
      size='sm'
      type='button'
      onClick={() => {
        if (previewing) {
          removeThemeVars()
          setTheme(themeBeforePreview.current)
          setCustomDomainPreview(undefined)
          onToggle(false)
          return
        }

        themeBeforePreview.current = currentScheme
        const attrs = form.getValues('customDomainAttributes')
        const scheme =
          attrs.themeMode && attrs.themeMode !== 'both'
            ? attrs.themeMode
            : selectedTab

        setTheme(scheme)

        applyThemeVars(attrs, scheme)
        setCustomDomainPreview({
          id: 0,
          themeMode: attrs.themeMode,
          generalTheme: attrs.generalTheme,
          darkModeTheme: attrs.darkModeTheme,
          lightModeTheme: attrs.lightModeTheme,
          darkModeBodyBackgroundPath: attrs.darkModeBodyBackgroundPath,
          lightModeBodyBackgroundPath: attrs.lightModeBodyBackgroundPath,
          darkModeHeaderLogoPath: attrs.darkModeHeaderLogoPath,
          lightModeHeaderLogoPath: attrs.lightModeHeaderLogoPath,
          headerConfig: attrs.headerConfig,
          headerShowName: attrs.headerShowName,
          guild: guild,
          team: team,
          active: true,
          hostsAttributes: [],
        })
        onToggle(true)
      }}
    >
      {previewing ? 'Stop preview' : 'Preview'}
    </Button>
  )
}

export const ThemeColorFields = ({
  previewing,
  selectedTab,
  setSelectedTab,
}: ThemeColorFieldsProps) => {
  const form = useFormContext()
  const { customDomain, setCustomDomainPreview } = useCustomDomain()
  const { setTheme } = useTheme()
  const { entityType, i18nPrefix } = useContext(FormEntityContext)
  const themeMode = form.watch('customDomainAttributes.themeMode')
  const { t } = useTranslation()

  const activeTab = themeMode === 'both' ? selectedTab : themeMode

  const colorFields = (scheme: 'light' | 'dark') => {
    const themeKey = scheme === 'dark' ? 'darkModeTheme' : 'lightModeTheme'

    return (
      <div key={scheme} className='whitespace-nowrap flex flex-col gap-2'>
        {Object.entries(themeDefaults).map(([category, vars]) => {
          return (
            <Card key={category} className='mb-4'>
              <Label className='block mb-3'>
                {t(
                  `${i18nPrefix}.${entityType}.customDomainAttributes.varCategories.${category}.label`,
                )}
              </Label>

              <div className='flex gap-2 flex-wrap'>
                {Object.entries(vars).map(([name, value]) => (
                  <ColorField
                    key={name}
                    customLabel={
                      <div className='flex gap-2 items-center'>
                        <span>
                          {t(
                            `${i18nPrefix}.${entityType}.customDomainAttributes.vars.${name}.label`,
                          )}
                        </span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info size={14} className='cursor-pointer' />
                          </TooltipTrigger>
                          <TooltipContent>
                            {t(
                              `${i18nPrefix}.${entityType}.customDomainAttributes.vars.${name}.tooltip`,
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    }
                    layout='inline-shrinkable'
                    name={`customDomainAttributes.${themeKey}.${name}`}
                    format='oklch'
                    originalValue={value[scheme]}
                    customDescription=''
                    onChange={(color) =>
                      previewing
                        ? document.documentElement.style.setProperty(
                            name,
                            color,
                          )
                        : null
                    }
                  />
                ))}
              </div>
            </Card>
          )
        })}

        <FileField
          name={`customDomainAttributes.${scheme}ModeBodyBackgroundPath`}
          onBlur={(e) => {
            if (previewing && customDomain) {
              setCustomDomainPreview({
                ...customDomain,
                [`${scheme}ModeBodyBackgroundPath`]: e.target.value,
              })
            }
          }}
        />

        <FileField
          name={`customDomainAttributes.${scheme}ModeHeaderLogoPath`}
          onBlur={(e) => {
            if (previewing && customDomain) {
              setCustomDomainPreview({
                ...customDomain,
                [`${scheme}ModeHeaderLogoPath`]: e.target.value,
              })
            }
          }}
        />
      </div>
    )
  }

  return (
    <Tabs
      value={activeTab}
      onValueChange={(value) => {
        setSelectedTab(value)

        if (previewing && themeMode === 'both') {
          setTheme(value as 'light' | 'dark')
          removeThemeVars()
          applyThemeVars(
            form.getValues('customDomainAttributes'),
            value as 'light' | 'dark',
          )
        }
      }}
    >
      {themeMode === 'both' && (
        <TabsList>
          <TabsTrigger value='dark'>Dark</TabsTrigger>
          <TabsTrigger value='light'>Light</TabsTrigger>
        </TabsList>
      )}
      <TabsContent value='dark'>{colorFields('dark')}</TabsContent>
      <TabsContent value='light'>{colorFields('light')}</TabsContent>
    </Tabs>
  )
}
